import * as React from 'react';
import axios from 'axios';
import { uniqBy } from 'lodash';
import { LearnInstance } from '@bb-learn-instances-service/common';
import { apiUrl } from 'utils/apiUrl';
import { useAuthContext } from 'contexts/AuthContext';

export interface LearnSitePages {
  sites: LearnInstance[];
  loading: boolean;
  /** The first failure of the last load. Regions that answered still show their sites. */
  error?: Error;
  /** Regions whose last request failed */
  failedRegions: string[];
  /**
   * Set while any region still has a page to load. It changes with every page, like the API's
   * own token, so callers can dedupe requests by it.
   */
  nextPageToken?: string;
  /** True once every region has returned its last page */
  allDataFetched: boolean;
  loadMore: (pageSize?: number) => Promise<void>;
}

/**
 * Pages through the Learn instances of one or more regions. The list API is per region, so each
 * region keeps its own page token and a load fetches the next page of every region still open.
 * A region that fails does not hide the others; it is retried with the next load.
 */
export const useLearnSitePages = (regions: string[], initialPageSize?: number): LearnSitePages => {
  const { idToken } = useAuthContext();
  // A token refresh must not throw away the pages already loaded, so requests read the
  // current token instead of depending on it
  const idTokenRef = React.useRef(idToken);
  idTokenRef.current = idToken;
  const regionsKey = regions.join(',');

  const [sites, setSites] = React.useState<LearnInstance[]>([]);
  // A region maps to its next page token; undefined once its last page is in
  const [tokens, setTokens] = React.useState<Record<string, string | undefined>>({});
  const [failedRegions, setFailedRegions] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | undefined>(undefined);

  // Responses for a region selection the user has moved away from are dropped
  const generationRef = React.useRef(0);
  const inFlightRef = React.useRef(false);

  const fetchPages = React.useCallback(
    async (requests: { region: string; nextPageToken?: string }[], pageSize?: number) => {
      const generation = generationRef.current;
      const headers = { Authorization: `Bearer ${idTokenRef.current}` };
      inFlightRef.current = true;
      setLoading(true);
      const responses = await Promise.allSettled(
        requests.map(({ region, nextPageToken }) =>
          axios.get(apiUrl('lct', `regions/${region}/instances/learnInstances`), {
            headers,
            params: {
              ...(nextPageToken ? { nextPageToken } : {}),
              ...(pageSize ? { pageSize } : {}),
            },
          }),
        ),
      );
      if (generation !== generationRef.current) return;

      const items: LearnInstance[] = responses.flatMap((response) =>
        response.status === 'fulfilled' ? (response.value.data?.items ?? []) : [],
      );
      // Later pages win when a site shows up twice
      setSites((prev) => uniqBy([...prev, ...items].reverse(), 'TenantId').reverse());
      setTokens((prev) => {
        const next = { ...prev };
        requests.forEach(({ region }, index) => {
          const response = responses[index];
          if (response.status === 'fulfilled') {
            next[region] = response.value.data?.nextPageToken;
          }
        });
        return next;
      });
      const failed = requests.filter((_, index) => responses[index].status === 'rejected');
      setFailedRegions(failed.map(({ region }) => region));
      const firstFailure = responses.find(
        (response): response is PromiseRejectedResult => response.status === 'rejected',
      );
      setError(firstFailure ? (firstFailure.reason as Error) : undefined);
      inFlightRef.current = false;
      setLoading(false);
    },
    [],
  );

  React.useEffect(() => {
    generationRef.current += 1;
    inFlightRef.current = false;
    setSites([]);
    setTokens({});
    setFailedRegions([]);
    setError(undefined);
    setLoading(false);
    fetchPages(
      regionsKey.split(',').map((region) => ({ region })),
      initialPageSize,
    );
    return () => {
      generationRef.current += 1;
    };
  }, [regionsKey, fetchPages, initialPageSize]);

  const openRegions = React.useMemo(
    () => Object.keys(tokens).filter((region) => tokens[region]),
    [tokens],
  );
  const allRegionsAnswered = regions.every((region) => region in tokens);

  const loadMore = React.useCallback(
    async (pageSize?: number) => {
      if (inFlightRef.current) return;
      // Regions whose first page failed have no token yet; ask for their first page again
      const unanswered = regionsKey.split(',').filter((region) => !(region in tokens));
      const requests = [
        ...openRegions.map((region) => ({ region, nextPageToken: tokens[region] })),
        ...unanswered.map((region) => ({ region })),
      ];
      if (requests.length === 0) return;
      await fetchPages(requests, pageSize);
    },
    [openRegions, tokens, regionsKey, fetchPages],
  );

  return {
    sites,
    loading,
    error,
    failedRegions,
    nextPageToken:
      openRegions.length > 0
        ? JSON.stringify(openRegions.map((region) => tokens[region]))
        : undefined,
    allDataFetched: allRegionsAnswered && openRegions.length === 0,
    loadMore,
  };
};

export default useLearnSitePages;
